import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import apiClient, { formatApiError } from "@/api/client";
import {
  Loader2, ChevronLeft, Sparkles, Copy, Download, Globe2, MessageSquare, Send,
  CheckCircle2, AlertCircle, FileText, Lightbulb, HelpCircle, Trash2, Search, ListChecks,
  Clock, DollarSign, Zap, ListTree, ListPlus, X, Plus,
} from "lucide-react";
import { toast } from "sonner";
import CustomerSelect from "@/components/CustomerSelect";

const MT_LABEL = { exact: "Tam", phrase: "Sıralı", broad: "Geniş" };
const MT_COLOR = { exact: "#16A34A", phrase: "#4F46E5", broad: "#F59E0B" };

const STATUS = {
  uploaded: { label: "Hazırlanıyor", color: "ky-badge-neutral" },
  preparing: { label: "Başlatılıyor", color: "ky-badge-neutral" },
  analyzing: { label: "Analiz ediliyor", color: "ky-badge-warning" },
  finalizing: { label: "Tamamlanıyor", color: "ky-badge-warning" },
  done: { label: "Tamamlandı", color: "ky-badge-success" },
  failed: { label: "Başarısız", color: "ky-badge-danger" },
};

export default function KeywordJobDetailPage() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [tab, setTab] = useState("positive"); // positive | negative | groups | chat
  const [groupBy, setGroupBy] = useState("flat"); // flat | groups
  const [overrideMt, setOverrideMt] = useState(null); // null | exact|phrase|broad
  const [chatMsg, setChatMsg] = useState("");
  const [chatSending, setChatSending] = useState(false);
  // Selection: per-tab Set of keyword strings (case-insensitive)
  const [selPos, setSelPos] = useState(new Set());
  const [selNeg, setSelNeg] = useState(new Set());
  const [addToListModal, setAddToListModal] = useState(null); // null | "positives" | "negatives"
  const [lists, setLists] = useState([]);
  const [chosenListId, setChosenListId] = useState("");
  const [newListName, setNewListName] = useState("");
  const [newListCustomerId, setNewListCustomerId] = useState("");
  const [allCustomers, setAllCustomers] = useState([]);
  const pollRef = useRef(null);
  const [loadError, setLoadError] = useState(null);
  const errCountRef = useRef(0);

  const load = async () => {
    try {
      const { data } = await apiClient.get(`/ai-tools/keyword/jobs/${jobId}`);
      setJob(data);
      setLoadError(null);
      errCountRef.current = 0;
    } catch (e) {
      // Silent retry for transient errors (502/503/504/timeout) — DON'T spam toasts during polling.
      // Only show a toast if it's a hard error (404, 403) on the FIRST call.
      const status = e?.response?.status;
      const isTransient = !status || [502, 503, 504, 408, 429].includes(status);
      errCountRef.current += 1;
      if (isTransient) {
        setLoadError({ kind: "transient", msg: `Bağlantı yeniden kuruluyor… (${errCountRef.current}. deneme)` });
      } else if (errCountRef.current === 1) {
        toast.error(formatApiError(e));
        setLoadError({ kind: "fatal", msg: formatApiError(e) });
      }
    }
  };
  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 4000);
    return () => clearInterval(pollRef.current);
  }, [jobId]); // eslint-disable-line

  // Stop polling when done/failed
  useEffect(() => {
    if (job?.status === "done" || job?.status === "failed") {
      if (pollRef.current) clearInterval(pollRef.current);
    }
  }, [job?.status]);

  const sendChat = async () => {
    if (!chatMsg.trim() || chatSending) return;
    setChatSending(true);
    try {
      await apiClient.post(`/ai-tools/keyword/jobs/${jobId}/chat`, { message: chatMsg });
      setChatMsg("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setChatSending(false); }
  };

  const publish = async (publish_) => {
    try {
      await apiClient.post(`/ai-tools/keyword/jobs/${jobId}/${publish_?"publish":"unpublish"}`);
      toast.success(publish_ ? "Müşteri paneline yayınlandı" : "Yayından kaldırıldı");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!job) return <KeywordJobSkeleton loadError={loadError}/>;

  const result = job.result || {};
  const positives = result.positive_keywords || [];
  const negatives = result.negative_keywords || [];
  const groupsPos = result.groups_positive || {};
  const groupsNeg = result.groups_negative || {};
  const st = STATUS[job.status] || STATUS.uploaded;
  const inProgress = ["preparing","analyzing","finalizing","uploaded"].includes(job.status);

  // Helpers for formatting keywords by match type
  const fmtKw = (kw, mt) => {
    const t = (overrideMt || mt || "broad").toLowerCase();
    if (t === "exact") return `[${kw}]`;
    if (t === "phrase") return `"${kw}"`;
    return kw;
  };

  const copyKeywords = (list, label) => {
    const text = list.map(k => fmtKw(k.keyword, k.match_type)).join("\n");
    navigator.clipboard.writeText(text);
    toast.success(`${label} (${list.length}) kopyalandı`);
  };

  const downloadCsv = (list, filename) => {
    const header = "Keyword,Match Type,Group,Rationale\n";
    const rows = list.map(k => `"${(k.keyword||"").replace(/"/g,'""')}","${overrideMt || k.match_type || ""}","${(k.group||"").replace(/"/g,'""')}","${(k.rationale||"").replace(/"/g,'""')}"`).join("\n");
    const blob = new Blob([header+rows], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const currentItems = tab === "positive" ? positives : negatives;
  const currentSel = tab === "positive" ? selPos : selNeg;
  const setCurrentSel = tab === "positive" ? setSelPos : setSelNeg;
  const toggleSel = (kw) => {
    const k = (kw || "").toLowerCase();
    const ns = new Set(currentSel);
    if (ns.has(k)) ns.delete(k); else ns.add(k);
    setCurrentSel(ns);
  };
  const selectAllCur = () => setCurrentSel(new Set(currentItems.map(k => (k.keyword || "").toLowerCase())));
  const clearSelCur = () => setCurrentSel(new Set());

  const openAddToList = async (target) => {
    try {
      const customer_id = job.customer_id || undefined;
      const [{ data }, { data: cs }] = await Promise.all([
        apiClient.get("/keyword-lists", { params: customer_id ? { customer_id } : {} }),
        apiClient.get("/customers"),
      ]);
      setLists(data || []);
      setAllCustomers(cs || []);
      setChosenListId(""); setNewListName(""); setNewListCustomerId(job.customer_id || "");
      setAddToListModal(target);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitAddToList = async () => {
    const sel = addToListModal === "positives" ? selPos : selNeg;
    const source = addToListModal === "positives" ? positives : negatives;
    const items = source.filter(k => sel.has((k.keyword || "").toLowerCase())).map(k => ({
      keyword: k.keyword, match_type: overrideMt || k.match_type || "broad",
      group: k.group, rationale: k.rationale, source_job_id: jobId,
    }));
    if (!items.length) return toast.error("Önce kelime seç");
    try {
      let listId = chosenListId;
      if (!listId) {
        if (!newListName.trim()) return toast.error("Liste adı gir veya mevcut liste seç");
        const targetCustomerId = newListCustomerId || job.customer_id || null;
        if (!targetCustomerId) return toast.error("Müşteri seçimi zorunlu — listeyi bir müşteriye bağlamalısın");
        const r = await apiClient.post("/keyword-lists", { name: newListName.trim(), customer_id: targetCustomerId });
        listId = r.data.id;
      }
      const r2 = await apiClient.post(`/keyword-lists/${listId}/items`, { target: addToListModal, items });
      const customerNote = (chosenListId ? "" : (newListCustomerId || job.customer_id) ? " · müşteri profiline eklendi" : "");
      toast.success(`${r2.data.added} kelime listeye eklendi${customerNote}`);
      setAddToListModal(null);
      if (addToListModal === "positives") setSelPos(new Set()); else setSelNeg(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="keyword-detail-page">
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <Link to="/ai-araclar/anahtar-kelime" className="hover:text-[#4F46E5] flex items-center gap-1"><ChevronLeft size={14}/>Anahtar Kelime Analizi</Link>
        <span>/</span>
        <span className="font-medium text-[#0F172A] truncate max-w-[300px]">{job.filename}</span>
      </div>

      {/* Header */}
      <div className="ky-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <FileText size={16} className="text-[#4F46E5]"/>
              <h1 className="text-xl font-bold truncate">{job.filename}</h1>
              <span className={`ky-badge ${st.color}`}>{st.label}</span>
              {job.published_to_portal && <span className="ky-badge ky-badge-success text-[10px]"><Globe2 size={9}/>Portal'da</span>}
            </div>
            <div className="text-xs text-[#64748B] space-x-3">
              <span>{job.mode === "search_terms" ? "Arama Terimleri" : "Keyword Planner"}</span>
              <span>·</span>
              <span>{(job.total_rows||0).toLocaleString("tr-TR")} satır</span>
              {job.prepared_rows !== undefined && <><span>·</span><span>{job.prepared_rows.toLocaleString("tr-TR")} benzersiz</span></>}
              {job.customer_id && <><span>·</span><span>Müşteri atandı</span></>}
            </div>
          </div>
          {job.status === "done" && (
            <div className="flex gap-2">
              {job.customer_id && (job.published_to_portal
                ? <button onClick={()=>publish(false)} className="ky-btn ky-btn-outline ky-btn-sm" data-testid="unpublish-btn"><Globe2 size={12}/>Portaldan Kaldır</button>
                : <button onClick={()=>publish(true)} className="ky-btn ky-btn-accent ky-btn-sm" data-testid="publish-btn"><Globe2 size={12}/>Müşteri Paneline Yayınla</button>
              )}
            </div>
          )}
        </div>

        {inProgress && (
          <ProgressPanel job={job}/>
        )}
        {(job.status === "done" || job.status === "failed") && job.cost && job.cost.calls > 0 && (
          <CostBar cost={job.cost} elapsed={job.elapsed_seconds} failed={job.failed_chunks}/>
        )}
        {job.status === "failed" && (
          <div className="mt-4 bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3 text-xs text-[#991B1B] flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 shrink-0"/>
            <div className="flex-1">
              <div>{job.status_message || "Bilinmeyen hata"}</div>
              {/^.*bütçe.*tükenmiş/i.test(job.status_message || "") && (
                <div className="mt-2 flex items-center gap-2">
                  <a href="https://console.anthropic.com/settings/billing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 bg-[#991B1B] text-white rounded text-[11px] font-medium hover:bg-[#7F1D1D]">
                    Anthropic Billing'i aç →
                  </a>
                  <span className="text-[10px] text-[#7F1D1D]">console.anthropic.com → Plans & Billing</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {job.status === "done" && (
        <>
          {/* AI suggestions + questions */}
          {(result.summary || (result.ai_suggestions||[]).length || (result.questions_for_user||[]).length) ? (
            <div className="grid lg:grid-cols-3 gap-4">
              {result.summary && (
                <div className="ky-card p-4 lg:col-span-3">
                  <div className="flex items-center gap-2 text-xs font-semibold text-[#4F46E5] mb-2"><Sparkles size={13}/>AI Analiz Özeti</div>
                  <p className="text-sm text-[#0F172A] leading-relaxed">{result.summary}</p>
                </div>
              )}
              {(result.ai_suggestions||[]).length > 0 && (
                <div className="ky-card p-4">
                  <div className="flex items-center gap-2 text-xs font-semibold text-[#16A34A] mb-2"><Lightbulb size={13}/>AI Önerileri</div>
                  <ul className="space-y-1.5 text-xs text-[#1F2937]">
                    {result.ai_suggestions.map((s,i)=><li key={i} className="flex gap-2"><span className="text-[#16A34A]">✓</span><span>{s}</span></li>)}
                  </ul>
                </div>
              )}
              {(result.questions_for_user||[]).length > 0 && (
                <div className="ky-card p-4 lg:col-span-2 bg-[#FEFCE8] border-[#FEF08A]">
                  <div className="flex items-center gap-2 text-xs font-semibold text-[#A16207] mb-2"><HelpCircle size={13}/>AI'ın Sana Sorduğu Sorular</div>
                  <ul className="space-y-1.5 text-xs text-[#713F12]">
                    {result.questions_for_user.map((q,i)=><li key={i} className="flex gap-2"><span>{i+1}.</span><span>{q}</span></li>)}
                  </ul>
                  <button onClick={()=>{ setTab("chat"); setTimeout(()=>document.getElementById("chat-input")?.focus(), 100); }} className="ky-btn ky-btn-outline ky-btn-sm mt-3"><MessageSquare size={11}/>Sohbette Cevapla</button>
                </div>
              )}
            </div>
          ) : null}

          {/* Tabs */}
          <div className="flex gap-1 border-b border-[#ECECEF] overflow-x-auto">
            {[
              ["positive", `Pozitif (${positives.length})`, Search],
              ["negative", `Negatif (${negatives.length})`, ListChecks],
              ["groups", "Gruplar", Sparkles],
              ["chat", `AI Sohbet${(job.messages||[]).length ? ` (${(job.messages||[]).length/2|0})` : ""}`, MessageSquare],
            ].map(([k,l,Ic]) => (
              <button key={k} onClick={()=>setTab(k)} className={`px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap flex items-center gap-1.5 ${tab===k?"border-[#4F46E5] text-[#4F46E5]":"border-transparent text-[#64748B] hover:text-[#0F172A]"}`} data-testid={`tab-${k}`}>
                <Ic size={13}/>{l}
              </button>
            ))}
          </div>

          {/* Action bar */}
          {(tab === "positive" || tab === "negative") && (
            <div className="ky-card p-3 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 text-xs text-[#64748B]">
                <span>Eşleşme türü:</span>
                <button onClick={()=>setOverrideMt(null)} className={`px-2 py-1 rounded text-xs ${overrideMt===null?"bg-[#4F46E5] text-white":"bg-[#F4F4F5] text-[#64748B]"}`}>AI Önerisi</button>
                {["exact","phrase","broad"].map(t => (
                  <button key={t} onClick={()=>setOverrideMt(t)} className={`px-2 py-1 rounded text-xs ${overrideMt===t?"bg-[#4F46E5] text-white":"bg-[#F4F4F5] text-[#64748B]"}`}>{MT_LABEL[t]}</button>
                ))}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-[#64748B]">
                <span className="ml-2">Görünüm:</span>
                <button onClick={()=>setGroupBy("flat")} className={`px-2 py-1 rounded text-xs ${groupBy==="flat"?"bg-[#4F46E5] text-white":"bg-[#F4F4F5] text-[#64748B]"}`}>Düz Liste</button>
                <button onClick={()=>setGroupBy("groups")} className={`px-2 py-1 rounded text-xs ${groupBy==="groups"?"bg-[#4F46E5] text-white":"bg-[#F4F4F5] text-[#64748B]"}`}>Gruplara Göre</button>
              </div>
              <div className="flex-1"/>
              {currentSel.size > 0 ? (
                <>
                  <span className="text-xs font-medium text-[#4F46E5]">{currentSel.size} seçili</span>
                  <button onClick={clearSelCur} className="ky-btn ky-btn-ghost ky-btn-sm text-[#EF4444]"><X size={11}/>Temizle</button>
                  <button onClick={()=>openAddToList(tab === "positive" ? "positives" : "negatives")} className="ky-btn ky-btn-accent ky-btn-sm" data-testid="add-to-list">
                    <ListPlus size={12}/>Listeye Ekle ({currentSel.size})
                  </button>
                </>
              ) : (
                <button onClick={selectAllCur} className="ky-btn ky-btn-outline ky-btn-sm" data-testid="select-all">Tümünü Seç</button>
              )}
              <button onClick={()=>copyKeywords(currentItems, tab==="positive"?"Pozitif":"Negatif")} className="ky-btn ky-btn-outline ky-btn-sm" data-testid="copy-all"><Copy size={12}/>Kopyala</button>
              <button onClick={()=>downloadCsv(currentItems, `${tab}-keywords.csv`)} className="ky-btn ky-btn-outline ky-btn-sm" data-testid="download-csv"><Download size={12}/>CSV</button>
            </div>
          )}

          {/* Positive list */}
          {tab === "positive" && (
            <KwList items={positives} groups={groupsPos} groupBy={groupBy} fmtKw={fmtKw} overrideMt={overrideMt} accent="#16A34A" emptyText="Pozitif kelime önerisi üretilmedi." sel={selPos} onToggle={toggleSel}/>
          )}
          {tab === "negative" && (
            <KwList items={negatives} groups={groupsNeg} groupBy={groupBy} fmtKw={fmtKw} overrideMt={overrideMt} accent="#EF4444" emptyText="Negatif kelime önerisi üretilmedi." sel={selNeg} onToggle={toggleSel}/>
          )}

          {tab === "groups" && (
            <div className="space-y-4">
              <GroupBlock title="Pozitif Anahtar Kelimeler" groups={groupsPos} fmtKw={fmtKw} overrideMt={overrideMt} accent="#16A34A"/>
              <GroupBlock title="Negatif Anahtar Kelimeler" groups={groupsNeg} fmtKw={fmtKw} overrideMt={overrideMt} accent="#EF4444"/>
            </div>
          )}

          {tab === "chat" && (
            <div className="ky-card p-0 flex flex-col" style={{ minHeight: 480 }}>
              <div className="flex-1 overflow-y-auto p-5 space-y-3 max-h-[60vh]">
                {(job.messages||[]).length === 0 && (
                  <div className="text-center text-xs text-[#94A3B8] py-12">
                    <MessageSquare size={28} className="mx-auto text-[#CBD5E1] mb-2"/>
                    AI ile sohbet et — sonuçları revize etmesini, kelime ekleyip çıkarmasını isteyebilirsin.
                    <br/>Örnek: "İstanbul bölgesindeki kelimeleri ayrı bir grupta topla", "Marka kelimelerini tüm listelerden çıkar".
                  </div>
                )}
                {(job.messages||[]).map((m,i) => (
                  <div key={i} className={`flex ${m.role==="user"?"justify-end":"justify-start"}`}>
                    <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${m.role==="user"?"bg-[#4F46E5] text-white":"bg-[#F4F4F5] text-[#0F172A]"}`}>
                      <div className="whitespace-pre-wrap">{m.content}</div>
                      <div className={`text-[10px] mt-1 ${m.role==="user"?"text-white/70":"text-[#94A3B8]"}`}>{new Date(m.ts).toLocaleString("tr-TR")}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-[#ECECEF] flex gap-2">
                <textarea id="chat-input" value={chatMsg} onChange={e=>setChatMsg(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter" && !e.shiftKey){ e.preventDefault(); sendChat(); }}} className="ky-textarea text-sm flex-1" rows={2} placeholder="AI'a mesaj yaz — kelime ekle/çıkar, yeniden grupla, ton değiştir…" data-testid="chat-input"/>
                <button onClick={sendChat} disabled={chatSending || !chatMsg.trim()} className="ky-btn ky-btn-accent" data-testid="chat-send">
                  {chatSending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add to List Modal */}
      {addToListModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={()=>setAddToListModal(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-xl" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-base flex items-center gap-2"><ListPlus size={16}/>Listeye Ekle</h3>
              <button onClick={()=>setAddToListModal(null)} className="ky-btn ky-btn-ghost ky-btn-icon"><X size={14}/></button>
            </div>
            <p className="text-xs text-[#64748B] mb-3">
              Seçilen <strong>{(addToListModal === "positives" ? selPos : selNeg).size}</strong> kelime
              {addToListModal === "positives" ? " pozitif" : " negatif"} olarak listeye eklenecek.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[#64748B] mb-1.5">Mevcut Listeye Ekle</label>
                <select value={chosenListId} onChange={e=>{ setChosenListId(e.target.value); setNewListName(""); }} className="ky-input" data-testid="choose-list">
                  <option value="">— yeni liste oluştur —</option>
                  {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              {!chosenListId && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-[#64748B] mb-1.5">Yeni Liste Adı *</label>
                    <input value={newListName} onChange={e=>setNewListName(e.target.value)} placeholder="Örn: İstanbul SEO Mart 2026" className="ky-input" data-testid="new-list-name-input"/>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#64748B] mb-1.5">Müşteri * <span className="text-[10px] font-normal text-[#94A3B8]">(zorunlu — liste profile + portal'da görünecek)</span></label>
                    <CustomerSelect customers={allCustomers} value={newListCustomerId || null} onChange={(v) => setNewListCustomerId(v || "")} placeholder="— müşteri seç —" required={!job.customer_id} testid="new-list-customer"/>
                    {(newListCustomerId || job.customer_id) ? (
                      <p className="text-[10px] text-[#10B981] mt-1">✓ Bu liste müşteri profilinde ve portalında görünecek</p>
                    ) : (
                      <p className="text-[10px] text-[#EF4444] mt-1">⚠️ Müşteri seçilmedi — liste kaydedilemez</p>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={()=>setAddToListModal(null)} className="ky-btn ky-btn-outline ky-btn-sm">İptal</button>
              <button onClick={submitAddToList} className="ky-btn ky-btn-accent ky-btn-sm" data-testid="confirm-add-to-list"><ListPlus size={12}/>Ekle</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const KwList = ({ items, groups, groupBy, fmtKw, overrideMt, accent, emptyText, sel, onToggle }) => {
  if (!items?.length) return <div className="ky-card p-10 text-center text-sm text-[#94A3B8]">{emptyText}</div>;
  const isSel = (kw) => sel?.has((kw || "").toLowerCase());
  if (groupBy === "flat") {
    return (
      <div className="ky-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFB] text-[11px] uppercase text-[#94A3B8]">
            <tr>
              <th className="text-left px-3 py-2 font-semibold w-10"></th>
              <th className="text-left px-4 py-2 font-semibold">Anahtar Kelime</th>
              <th className="text-left px-4 py-2 font-semibold">Eşleşme</th>
              <th className="text-left px-4 py-2 font-semibold">Grup</th>
              <th className="text-left px-4 py-2 font-semibold">Gerekçe</th>
            </tr>
          </thead>
          <tbody>
            {items.map((k,i) => {
              const mt = (overrideMt || k.match_type || "broad").toLowerCase();
              const sd = isSel(k.keyword);
              return (
                <tr key={i} className={`border-t border-[#ECECEF] ${sd?"bg-[#EEF2FF]":"hover:bg-[#FAFAFB]"}`} data-testid={`kw-row-${i}`}>
                  <td className="px-3 py-2.5"><input type="checkbox" checked={!!sd} onChange={()=>onToggle && onToggle(k.keyword)} className="w-4 h-4 accent-[#4F46E5]" data-testid={`kw-checkbox-${i}`}/></td>
                  <td className="px-4 py-2.5 font-mono font-medium text-[13px]">{fmtKw(k.keyword, k.match_type)}</td>
                  <td className="px-4 py-2.5"><span className="ky-badge text-[10px]" style={{background:MT_COLOR[mt]+"22", color:MT_COLOR[mt]}}>{MT_LABEL[mt] || mt}</span></td>
                  <td className="px-4 py-2.5 text-xs text-[#64748B]">{k.group || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-[#94A3B8] max-w-md">{k.rationale || ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {Object.entries(groups || {}).map(([gname, gitems]) => (
        <GroupCard key={gname} name={gname} items={gitems} fmtKw={fmtKw} overrideMt={overrideMt} accent={accent} sel={sel} onToggle={onToggle}/>
      ))}
    </div>
  );
};

const GroupBlock = ({ title, groups, fmtKw, overrideMt, accent }) => {
  const entries = Object.entries(groups || {});
  if (!entries.length) return null;
  return (
    <div>
      <h3 className="font-semibold text-sm mb-2" style={{color: accent}}>{title}</h3>
      <div className="space-y-2">
        {entries.map(([gname, gitems]) => (
          <GroupCard key={gname} name={gname} items={gitems} fmtKw={fmtKw} overrideMt={overrideMt} accent={accent}/>
        ))}
      </div>
    </div>
  );
};

const GroupCard = ({ name, items, fmtKw, overrideMt, accent, sel, onToggle }) => {
  const copy = () => {
    const text = items.map(k => fmtKw(k.keyword, k.match_type)).join("\n");
    navigator.clipboard.writeText(text);
    toast.success(`${name} grubu (${items.length}) kopyalandı`);
  };
  const isSel = (kw) => sel?.has((kw || "").toLowerCase());
  return (
    <div className="ky-card p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-4 rounded-full" style={{background:accent}}/>
          <span className="font-medium text-sm">{name}</span>
          <span className="ky-badge ky-badge-neutral text-[10px]">{items.length}</span>
        </div>
        <button onClick={copy} className="ky-btn ky-btn-ghost ky-btn-sm text-[#64748B]"><Copy size={11}/>Kopyala</button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((k,i) => {
          const sd = isSel(k.keyword);
          return (
            <button key={i} title={k.rationale || ""}
              onClick={()=>onToggle && onToggle(k.keyword)}
              className={`px-2 py-1 rounded-md text-xs font-mono border transition-all ${sd?"bg-[#EEF2FF] border-[#4F46E5] text-[#4F46E5]":"bg-[#F8FAFC] border-[#ECECEF] text-[#0F172A] hover:border-[#C7D2FE]"}`}
              data-testid={`kw-chip-${i}`}>
              {sd && <span className="mr-1">✓</span>}
              {fmtKw(k.keyword, k.match_type)}
            </button>
          );
        })}
      </div>
    </div>
  );
};


// ============ Progress + Cost UI ============
const STEP_LABELS = {
  parsing: { label: "CSV ayrıştırılıyor", icon: FileText },
  chunking: { label: "Kelimeler gruplandırılıyor", icon: ListTree },
  consolidation: { label: "Sonuçlar konsolide ediliyor", icon: Sparkles },
  done: { label: "Tamamlandı", icon: CheckCircle2 },
};

const ProgressPanel = ({ job }) => {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => { const t = setInterval(()=>setNow(Date.now()), 1000); return ()=>clearInterval(t); }, []);
  const startedMs = job.started_at ? new Date(job.started_at).getTime() : (job.created_at ? new Date(job.created_at).getTime() : now);
  const elapsedSec = Math.max(0, Math.floor((now - startedMs) / 1000));
  const fmt = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,"0")}`;
  // ETA: linear extrapolation from progress
  const p = job.progress || 0;
  const etaSec = p > 5 && p < 100 ? Math.max(0, Math.floor(elapsedSec * (100 - p) / p)) : null;

  const stepKey = (job.step || "").startsWith("chunk_") ? "chunking" : (job.step || "parsing");
  const stepInfo = STEP_LABELS[stepKey] || { label: job.status_message || "İşleniyor", icon: Loader2 };
  const StepIcon = stepInfo.icon;
  const cost = job.cost || {};

  // Parse "chunk_N_of_M" to show count
  const chunkMatch = /^chunk_(\d+)_of_(\d+)$/.exec(job.step || "");
  const chunkText = chunkMatch ? `Grup ${chunkMatch[1]}/${chunkMatch[2]}` : null;

  return (
    <div className="mt-4 space-y-3">
      <div>
        <div className="flex items-center justify-between text-xs mb-1.5">
          <div className="flex items-center gap-2 text-[#0F172A] font-medium">
            <StepIcon size={13} className={stepKey !== "done" ? "animate-spin" : ""} style={{ animationDuration: "2s" }}/>
            <span>{stepInfo.label}</span>
            {chunkText && <span className="text-[#94A3B8] font-normal">· {chunkText}</span>}
          </div>
          <span className="font-semibold text-[#4F46E5]">{p}%</span>
        </div>
        <div className="h-2 bg-[#F4F4F5] rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] transition-all duration-500" style={{ width: `${p}%` }}/>
        </div>
        {job.status_message && <div className="text-[11px] text-[#64748B] mt-1.5">{job.status_message}</div>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat icon={Clock} label="Geçen Süre" value={fmt(elapsedSec)} color="#4F46E5"/>
        <Stat icon={Clock} label="Tahmini Kalan" value={etaSec !== null ? `~${fmt(etaSec)}` : "—"} color="#7C3AED"/>
        <Stat icon={Zap} label="AI Çağrısı" value={cost.calls || 0} color="#F59E0B"/>
        <Stat icon={DollarSign} label="Maliyet" value={cost.usd ? `$${cost.usd.toFixed(4)}` : "$0"} sub={cost.try ? `₺${cost.try.toFixed(2)}` : null} color="#16A34A"/>
      </div>

      {/* Cancel button — shown when job is in non-terminal state and elapsed > 30s */}
      {elapsedSec > 30 && (
        <div className="flex items-center justify-between bg-[#FFFBEB] border border-[#FDE68A] rounded-lg px-3 py-2 text-xs">
          <span className="text-[#92400E]">Takıldı mı? İş 30sn+ sürdüyse iptal edip yeniden başlatabilirsin.</span>
          <button onClick={async () => {
            if (!window.confirm("Bu analizi iptal etmek istediğine emin misin?")) return;
            try {
              await apiClient.post(`/ai-tools/keyword/jobs/${job.id}/cancel`);
              toast.success("İptal edildi");
              window.location.reload();
            } catch (e) { toast.error(formatApiError(e)); }
          }} className="ky-btn ky-btn-outline ky-btn-sm text-[#92400E] border-[#FDE68A]" data-testid="cancel-keyword-job">İptal Et</button>
        </div>
      )}
    </div>
  );
};

const CostBar = ({ cost, elapsed, failed }) => (
  <div className="mt-4 flex items-center justify-between flex-wrap gap-2 bg-[#FAFAFB] border border-[#ECECEF] rounded-lg px-3 py-2 text-xs">
    <div className="flex items-center gap-4 flex-wrap">
      <span className="flex items-center gap-1.5 text-[#64748B]"><Clock size={12}/>Süre: <strong className="text-[#0F172A]">{Math.floor((elapsed||0)/60)}dk {Math.floor((elapsed||0)%60)}sn</strong></span>
      <span className="flex items-center gap-1.5 text-[#64748B]"><Zap size={12}/>AI çağrısı: <strong className="text-[#0F172A]">{cost.calls || 0}</strong></span>
      <span className="flex items-center gap-1.5 text-[#64748B]"><ListTree size={12}/>Token: <strong className="text-[#0F172A]">{(cost.total_tokens || 0).toLocaleString("tr-TR")}</strong></span>
      <span className="flex items-center gap-1.5 text-[#64748B]"><DollarSign size={12}/>Maliyet: <strong className="text-[#0F172A]">${(cost.usd || 0).toFixed(4)}</strong> <span className="text-[#94A3B8]">(₺{(cost.try || 0).toFixed(2)})</span></span>
    </div>
    {failed > 0 && <span className="text-[#F59E0B] flex items-center gap-1.5"><AlertCircle size={12}/>{failed} grup geçici hata aldı</span>}
  </div>
);

const Stat = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-[#FAFAFB] border border-[#ECECEF] rounded-lg p-2.5">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold mb-0.5" style={{color}}>
      <Icon size={11}/>{label}
    </div>
    <div className="text-base font-bold text-[#0F172A] font-mono">{value}</div>
    {sub && <div className="text-[10px] text-[#94A3B8] font-mono">{sub}</div>}
  </div>
);


// Engaging loading state — shown while waiting for first job fetch or during transient 502s.
// Animates through analiz steps with a rotating focus indicator + tips carousel.
const SKELETON_STEPS = [
  { icon: FileText, label: "İş yükleniyor", hint: "Analiz kaydı veritabanından getiriliyor…" },
  { icon: ListTree, label: "AI motoru hazırlanıyor", hint: "Claude Sonnet 4.5 oturumu açılıyor…" },
  { icon: Sparkles, label: "Sonuçlar derleniyor", hint: "İlerleme bilgileri birazdan ekrana akacak…" },
];
const SKELETON_TIPS = [
  "İpucu: Anahtar kelimeler gruplara ayrılır (Marka, Hizmet, Bölge, Niyet, Rakip).",
  "İpucu: Her kelime için AI eşleşme türü önerisi sunar — Tam / Sıralı / Geniş.",
  "İpucu: Analiz tamamlandığında sohbet aracılığıyla revizyon isteyebilirsin.",
  "İpucu: Beğendiğin kelimeleri seçip 'Listeye Ekle' diyerek müşteri profiline kaydedebilirsin.",
  "İpucu: Sonuçları CSV olarak indirebilir, Google Ads'e doğrudan yapıştırabilirsin.",
];

const KeywordJobSkeleton = ({ loadError }) => {
  const [stepIdx, setStepIdx] = React.useState(0);
  const [tipIdx, setTipIdx] = React.useState(0);
  React.useEffect(() => {
    const t1 = setInterval(() => setStepIdx(i => (i + 1) % SKELETON_STEPS.length), 1800);
    const t2 = setInterval(() => setTipIdx(i => (i + 1) % SKELETON_TIPS.length), 4000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);
  const Active = SKELETON_STEPS[stepIdx].icon;
  return (
    <div className="max-w-2xl mx-auto py-12" data-testid="keyword-job-skeleton">
      <div className="ky-card p-8 text-center">
        <div className="relative w-20 h-20 mx-auto mb-5">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] opacity-10 animate-pulse"/>
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] flex items-center justify-center">
            <Active size={32} className="text-white animate-pulse" style={{ animationDuration: "1.5s" }}/>
          </div>
          <div className="absolute -inset-1 rounded-2xl border-2 border-[#4F46E5] opacity-30 animate-ping" style={{ animationDuration: "2s" }}/>
        </div>
        <h2 className="text-lg font-bold text-[#0F172A]">{SKELETON_STEPS[stepIdx].label}</h2>
        <p className="text-sm text-[#64748B] mt-1">{SKELETON_STEPS[stepIdx].hint}</p>

        <div className="mt-6 flex items-center justify-center gap-2">
          {SKELETON_STEPS.map((s, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all duration-500 ${i === stepIdx ? "w-8 bg-[#4F46E5]" : "w-1.5 bg-[#E5E7EB]"}`}/>
          ))}
        </div>

        {loadError && (
          <div className={`mt-5 inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full ${loadError.kind === "transient" ? "bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]" : "bg-[#FEF2F2] text-[#991B1B] border border-[#FECACA]"}`}>
            {loadError.kind === "transient" ? <Loader2 size={11} className="animate-spin"/> : <AlertCircle size={11}/>}
            <span>{loadError.msg}</span>
          </div>
        )}

        <div className="mt-6 pt-5 border-t border-[#ECECEF]">
          <div className="text-[11px] uppercase tracking-wider text-[#94A3B8] font-semibold mb-2 flex items-center justify-center gap-1.5">
            <Lightbulb size={11}/>Bunu biliyor muydun?
          </div>
          <div className="text-xs text-[#475569] min-h-[2.5em] flex items-center justify-center px-4">
            <span key={tipIdx} className="ky-fade-in">{SKELETON_TIPS[tipIdx]}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
